import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { MemberService, MemberType } from 'app/cd/services/member-service';
import { apiCall } from 'app/shared/lib/api/api';
import { openProviderSummary } from 'app/cd/components/summary/provider/provider-summary';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import { openPatientSummary } from 'app/pp/components/summary/patient-summary';
import { ICustomFieldTargetColumnsContainer, ICustomFieldTargetColumnsSettings } from 'app/shared/lib/custom-field-definitions';
import { CustomFieldContext } from 'app/core/lib/custom-field-context';
import * as CD from 'app/cd/models/essette-api';

@autoinject()
export class MemberInformation extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public memberId: string | null = null;

    @bindable.string({ defaultBindingMode: bindingMode.toView })
    protected memberType: MemberType = MemberType.Member;

    @bindable.boolean
    protected enableMemberLink: boolean = true;

    protected member: CD.Member | null = null;

    protected customFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;

    protected showMemberSummaryLink: boolean = false;
    protected showProviderSummaryLink: boolean = false;
    protected openMemberSummary = openMemberSummary;
    protected openPatientSummary = openPatientSummary;
    protected openProviderSummary = openProviderSummary;

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        private readonly customFieldContext: CustomFieldContext,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        if(this.memberType === MemberType.Member) this.loadCustomFieldTargets();
        this.checkPermissions();
        this.loadMember();
    }

    ////****************** Events ***************************************************////
    public async reload(): Promise<void> {
        await this.loadMember();
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.showMemberSummaryLink = this.enableMemberLink && (this.memberType === MemberType.Patient ? this.accountContext.hasRouteAccess('pp.patient.summary') : this.accountContext.hasRouteAccess('cd.member.summary'));
        this.showProviderSummaryLink = this.accountContext.hasRouteAccess('cd.provider.summary');
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadCustomFieldTargets(): Promise<void> {
        const settings: ICustomFieldTargetColumnsSettings = {
            definitionCount: 25,
            filterInactive: true,
            sortByLabel: true
        };
        this.customFieldTargetColumns = await this.customFieldContext.getCustomFieldTargetColumns('CD.Member', settings);
    }

    @apiCall
    private async loadMember(): Promise<void> {
        if(!this.memberId) return;

        const includes = [
            'address.mailing',
            'gender',
            'language.reads',
            'language.speaks',
            'phoneNumber.home',
            'primaryCarePhysician',
            'primaryCarePhysician.location',
            'primaryCarePhysician.locations.facility',
            'primaryCarePhysician.locations.facility.phoneNumber.primary',
            'primaryPlan.healthPlan'
        ];

        const fields = [
            'address.mailing.city',
            'address.mailing.street1',
            'address.mailing.street2',
            'address.mailing.postalCode',
            'address.mailing.region.code',
            'age.display',
            'birthDate',
            'description',
            'gender.display',
            'identifier.primary',
            'language.reads.description',
            'language.speaks.description',
            'name.full',
            'phoneNumber.home.country.phoneNumber.callingCode',
            'phoneNumber.home.extension',
            'phoneNumber.home.number',
            'primaryCarePhysician.description',
            'primaryCarePhysician.location.description',
            'primaryCarePhysician.locations.facility.phoneNumber.primary.country.phoneNumber.callingCode',
            'primaryCarePhysician.locations.facility.phoneNumber.primary.extension',
            'primaryCarePhysician.locations.facility.phoneNumber.primary.number',
            'primaryPlan.healthPlan.description'
        ];

        // do not include UDF data in portal
        if(this.memberType === MemberType.Member) {
            includes.push('custom');
            fields.push('custom.text.*', 'custom.lookup.*');
        }

        try {
            this.state.beginLoad();

            const response = this.memberType === MemberType.Patient ? await this.memberService.loadPatient(this.memberId, includes, fields) : await this.memberService.loadMember(this.memberId, includes, fields);
            this.member = response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
